import React from 'react';
import { useLocation } from 'react-router-dom';
import SEO from '@/components/SEO';
import { getBaseJsonLd } from '@/config/seo';

const ROUTE_SEO = {
  '/login': {
    title: 'Caterer Login | Sign In',
    description:
      'Sign in to your Mrs Cater caterer account to manage your profile, menu, bids, orders and payouts.',
    keywords:
      'caterer login, Mrs Cater sign in, caterer dashboard, catering business management, event catering',
  },
  '/onboarding': {
    title: 'Activate Your Caterer Profile',
    description:
      'Set up your Mrs Cater caterer profile, menu and availability to receive event requests and manage bookings.',
    keywords:
      'activate caterer profile, caterer onboarding, manage catering profile, event catering platform, Mrs Cater partner portal',
  },
  '/dashboard': {
    title: 'Dashboard',
    description: 'Your Mrs Cater caterer dashboard. Manage orders, bids, and revenue.',
  },
  '/orders': { title: 'Orders', description: 'Manage your catering orders on Mrs Cater.' },
  '/menu-management': { title: 'Menu Management', description: 'Manage your catering menu on Mrs Cater.' },
  '/bid-management': { title: 'Bids', description: 'Manage bids and event requests on Mrs Cater.' },
  '/messages': { title: 'Messages', description: 'Customer messages on Mrs Cater.' },
  '/wallet': { title: 'Wallet', description: 'Payouts and wallet on Mrs Cater.' },
  '/reviews': { title: 'Reviews', description: 'Customer reviews on Mrs Cater.' },
  '/business-profile': { title: 'Business Profile', description: 'Your caterer business profile on Mrs Cater.' },
  '/portfolio': { title: 'Portfolio', description: 'Your portfolio on Mrs Cater.' },
  '/verification/upgrade': { title: 'Verification Upgrade', description: 'Upgrade your caterer verification on Mrs Cater.' },
  '/verification/success': { title: 'Verification Success', description: 'Verification complete on Mrs Cater.' },
};

function CatererSEO() {
  const { pathname } = useLocation();
  const base = pathname.replace(/\/$/, '') || '/';
  const config = ROUTE_SEO[base] || ROUTE_SEO[pathname] || {};

  const baseJsonLd = getBaseJsonLd();

  // Add HowTo schema specifically for the public caterer onboarding flow
  if (base === '/onboarding') {
    baseJsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'HowTo',
      name: 'How to onboard your catering business on Mrs Cater',
      description:
        'Step-by-step guide to activate your caterer profile, add business details, set availability, and publish your menu to receive event requests.',
      step: [
        {
          '@type': 'HowToStep',
          name: 'Create your caterer account',
          text: 'Sign up or log in with your email address to access the Mrs Cater caterer dashboard.',
        },
        {
          '@type': 'HowToStep',
          name: 'Add your business details',
          text: 'Enter your business name, description, cuisine types, and service areas so customers can find you.',
        },
        {
          '@type': 'HowToStep',
          name: 'Set your operating hours',
          text: 'Configure your opening days and hours so customers know when you are available for events.',
        },
        {
          '@type': 'HowToStep',
          name: 'Create your catering menu',
          text: 'Add menu items, prices, and serving formats that you want to offer for events.',
        },
        {
          '@type': 'HowToStep',
          name: 'Publish and start receiving requests',
          text: 'Submit your profile for verification and start receiving catering requests and bids from customers.',
        },
      ],
    });
  }

  return (
    <SEO
      title={config.title}
      description={config.description}
      keywords={config.keywords}
      canonical={pathname}
      jsonLd={baseJsonLd}
      noindex={config.noindex}
    />
  );
}

export default CatererSEO;
